import { useApolloClient, useMutation, useSuspenseQuery } from "@apollo/client";
import { AlertTriangleIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Text } from "@/DesignSystem/basic/Text/Text";
import { Input } from "@/DesignSystem/shadcn/Input";
import { EditExperimentInput, FragmentType, gql, useFragment } from "@/apis/nannyml";
import { DataSourceOverview } from "@/components/DataSource";
import { confirm } from "@/components/Dialog";
import { LabeledField } from "@/components/LabeledField";
import { Button } from "@/components/common/Button";
import { useToast } from "@/components/common/Toast/useToast";
import { ExperimentMetricConfig } from "@/components/experiment";
import { experimentTypeLabels } from "@/formatters/experiment";
import { useExperimentId } from "@/hooks/experiment";
import { formatISODateTime } from "@/lib/dateUtils";
import { omitRecursively } from "@/lib/objUtils";
import { cn } from "@/lib/utils";

type StagedChanges = Partial<EditExperimentInput>;
type StageChangeFn = (change: Partial<EditExperimentInput>) => void;

const experimentMetadataFragment = gql(/* GraphQL */ `
  fragment ExperimentMetadataFragment on Experiment {
    id
    name
    experimentType
    kem
    createdAt
  }
`);

const experimentMetricConfigFragment = gql(/* GraphQL */ `
  fragment ExperimentMetricConfigFragment on Experiment {
    kem
    runtimeConfig {
      metrics {
        metric
        ropeLowerBound
        ropeUpperBound
        hdiWidth
      }
    }
  }
`);

const experimentDataOverviewFragment = gql(/* GraphQL */ `
  fragment ExperimentDataOverviewFragment on Experiment {
    id
    dataSource {
      ...DataSourceDetails
    }
  }
`);

const experimentSettingsQuery = gql(/* GraphQL */ `
  query GetExperimentSettings($experimentId: Int!) {
    experiment(id: $experimentId) {
      ...ExperimentMetadataFragment
      ...ExperimentMetricConfigFragment
      ...ExperimentDataOverviewFragment
    }
  }
`);

const editExperimentMutation = gql(/* GraphQL */ `
  mutation EditExperiment($input: EditExperimentInput!) {
    edit_experiment(input: $input) {
      ...ExperimentMetadataFragment
      ...ExperimentMetricConfigFragment
    }
  }
`);

const deleteExperimentMutation = gql(/* GraphQL */ `
  mutation DeleteExperiment($experimentId: Int!) {
    delete_experiment(id: $experimentId) {
      id
    }
  }
`);

export const ExperimentSettings = () => {
  const client = useApolloClient();
  const experimentId = useExperimentId();
  const { resolveWithToast } = useToast();
  const [stagedChanges, setStagedChanges] = useState<EditExperimentInput>({ experimentId });
  const { data } = useSuspenseQuery(experimentSettingsQuery, { variables: { experimentId } });
  const [editExperiment, { loading }] = useMutation(editExperimentMutation, {
    onCompleted: () => {
      setStagedChanges({ experimentId });
      client.cache.evict({ id: `Experiment:${experimentId}`, fieldName: "results" });
    },
  });

  const experiment = data.experiment;
  if (!experiment) {
    throw new Error(`Experiment '${experimentId}' not found`);
  }

  const stageChange = useCallback(
    (change: Partial<EditExperimentInput>) => setStagedChanges((prev) => ({ ...prev, ...change })),
    [setStagedChanges]
  );

  return (
    <div className="p-4 flex flex-col 2xl:flex-row gap-8">
      <form
        className="flex flex-col gap-8 items-center"
        onSubmit={(e) => {
          e.preventDefault();

          const promise = editExperiment({ variables: { input: stagedChanges } });
          resolveWithToast(promise, {
            title: "Changing experiment settings...",
            successDescription: "✅ Experiment settings changed",
            errorDescription: "❌ Failed to change experiment settings",
          });
        }}
      >
        <Section title="Experiment settings">
          <GeneralSettings value={experiment} stageChange={stageChange} stagedChanges={stagedChanges} />
        </Section>
        <Section title="Metrics">
          <MetricSettings value={experiment} stageChange={stageChange} stagedChanges={stagedChanges} />
        </Section>
        <Button
          cva={{ intent: "primary" }}
          type="submit"
          disabled={Object.values(stagedChanges).length <= 1 || loading}
        >
          Save changes
        </Button>
      </form>
      <div className="flex flex-col gap-8">
        <Section title="Data">
          <DataOverview experiment={experiment} />
        </Section>
        <Section title="Danger zone" className="[&>p]:text-alert">
          <DangerZone experiment={experiment} />
        </Section>
      </div>
    </div>
  );
};

const Section = ({ children, className, title }: { children: React.ReactNode; className?: string; title: string }) => (
  <div className={cn("w-full space-y-4", className)}>
    <Text className="pb-1 border-b border-gray-600" size="xLarge" stroke="boldLight">
      {title}
    </Text>
    {children}
  </div>
);

const GeneralSettings = ({
  value,
  stageChange,
  stagedChanges,
}: {
  value: FragmentType<typeof experimentMetadataFragment>;
  stageChange: StageChangeFn;
  stagedChanges: StagedChanges;
}) => {
  const experiment = useFragment(experimentMetadataFragment, value);

  return (
    <div className="grid grid-cols-3 gap-4">
      <LabeledField label="Experiment name" className="col-span-full">
        <Input
          type="text"
          value={stagedChanges?.name ?? experiment.name}
          onChange={(e) => stageChange({ name: e.target.value })}
        />
      </LabeledField>
      <LabeledField label="Experiment type">{experimentTypeLabels[experiment.experimentType]}</LabeledField>
      <LabeledField label="KEM">{stagedChanges.kem ?? experiment.kem}</LabeledField>
      <LabeledField label="Created at">{formatISODateTime(new Date(experiment.createdAt))}</LabeledField>
    </div>
  );
};

const MetricSettings = ({
  value,
  stageChange,
  stagedChanges,
}: {
  value: FragmentType<typeof experimentMetricConfigFragment>;
  stageChange: StageChangeFn;
  stagedChanges: StagedChanges;
}) => {
  const experiment = useFragment(experimentMetricConfigFragment, value);
  return (
    <ExperimentMetricConfig
      value={{
        kem: stagedChanges.kem ?? experiment.kem,
        config: stagedChanges.config?.metrics
          ? stagedChanges.config
          : omitRecursively(experiment.runtimeConfig, "__typename"),
      }}
      onChange={stageChange}
    />
  );
};

const DataOverview = (props: { experiment: FragmentType<typeof experimentDataOverviewFragment> }) => {
  const experiment = useFragment(experimentDataOverviewFragment, props.experiment);

  return <DataSourceOverview dataSources={[experiment.dataSource]} />;
};

const DangerZone = (props: { experiment: FragmentType<typeof experimentMetadataFragment> }) => {
  const navigate = useNavigate();
  const { resolveWithToast } = useToast();
  const [deleteExperiment, { loading }] = useMutation(deleteExperimentMutation);
  const experiment = useFragment(experimentMetadataFragment, props.experiment);

  const deleteExperimentWithToast = () => {
    const promise = deleteExperiment({ variables: { experimentId: experiment.id } }).then(() =>
      navigate("/experiment")
    );
    resolveWithToast(promise, {
      title: "Deleting experiment...",
      successDescription: "✅ Experiment deleted",
      errorDescription: "❌ Failed to delete experiment",
      retry: deleteExperimentWithToast,
    });
  };

  const onDeleteExperiment = () => {
    confirm({
      title: "Delete experiment",
      message: <DeleteExperimentDialog experimentName={experiment.name} />,
      confirmLabel: "Delete",
      cancelIntent: "primary",
      confirmIntent: "secondary",
    }).then((confirmed) => {
      if (confirmed) {
        deleteExperimentWithToast();
      }
    });
  };

  return (
    <div>
      <p className="text-gray-400 text-sm mt-2">
        This section allows you to make changes to the experiment that could break things. Make sure you know what
        you're doing before applying changes.
      </p>
      <div className="flex justify-between mt-4 items-center gap-4">
        <div className="flex flex-col">
          <p className="font-bold">Delete experiment</p>
          <p className="text-sm text-gray-400">
            Deleting a experiment will permanently delete all its results, data and settings. This cannot be undone.
          </p>
        </div>
        <Button
          label={"Delete experiment"}
          cva={{ intent: "reject" }}
          className="p-2 justify-center whitespace-nowrap h-fit"
          onClick={onDeleteExperiment}
          disabled={loading}
        />
      </div>
    </div>
  );
};

const DeleteExperimentDialog = ({ experimentName }: { experimentName: string }) => (
  <div className="flex mb-3 items-center">
    <AlertTriangleIcon className="inline-block mr-5 text-warningPale" size={64} />
    <div className="flex flex-col gap-3">
      <p>
        Deleting an experiment will <b>permanently delete</b> all its results, data and settings. This cannot be undone.
      </p>
      <p>Are you sure you want to delete experiment '{experimentName}'?</p>
    </div>
  </div>
);
